import React, { useState } from "react";
import { Trophy, CaretRight } from "@phosphor-icons/react";
import { useAnalytics } from "@/components/assets/useAssets";
import { Panel, ErrorState, EmptyState, SkeletonRows, Pager, AssetName, TokenLogo, Delta, MiniSpark, fmtInt } from "@/components/assets/parts";
import { useRequestMeta } from "@/components/assets/tokenMeta";

const COUNT = 10;

// Trend cell: a 2-point (previous window -> current) spark. A full per-asset
// daily line isn't viable here — Top Assets are high-activity, so a bounded tx
// window spans <1 day and the endpoint times out, giving no multi-day spread.
const TrendCell = ({ it, i }) => {
  const up = (it.tx_count || 0) >= (it.previous_tx_count || 0);
  const hasPrev = it.previous_tx_count != null && it.previous_tx_count > 0;
  return (
    <span className="inline-flex justify-end w-full" data-testid={`topasset-trend-${i}`}>
      {hasPrev ? (
        <MiniSpark values={[it.previous_tx_count, it.tx_count]} color={up ? "#34d399" : "#fb7185"} width={56} height={22} />
      ) : (
        <span className="text-[10px] mono text-[#22d3ee]">new</span>
      )}
    </span>
  );
};


export const TopAssetsTable = ({ win = "24h", onPickAsset, onOpenPolicy, onOpenExplorer }) => {
  const [page, setPage] = useState(1);
  const { data, loading, error, reload } = useAnalytics(
    "/assets/trending",
    { window: win, count: COUNT, page },
    { ttl: 120000, refreshMs: 120000 },
  );
  const items = data?.items || [];
  useRequestMeta(items.map((it) => it.asset));

  // Reset to page 1 whenever the shared window changes.
  const winRef = React.useRef(win);
  if (winRef.current !== win) {
    winRef.current = win;
    if (page !== 1) setPage(1);
  }

  return (
    <Panel
      title="Top native assets by activity"
      icon={Trophy}
      right={
        <button
          type="button"
          data-testid="open-asset-explorer"
          onClick={() => onOpenExplorer?.()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/15 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.14em] mono text-primary transition-colors glow-active"
        >
          Open Asset Explorer <CaretRight size={11} weight="bold" />
        </button>
      }
      testid="top-assets"
    >
      <div className="px-5 pt-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground/60 mono">
        ranked by transaction-count growth vs the previous {win} — not price or trading volume
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] mono tabnum min-w-[640px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
              <th className="text-right font-sans font-medium px-4 py-2.5 border-b border-primary/10 w-10">#</th>
              <th className="text-left font-sans font-medium px-4 py-2.5 border-b border-primary/10">Asset name</th>
              <th className="text-left font-sans font-medium px-4 py-2.5 border-b border-primary/10">Fingerprint</th>
              <th className="text-right font-sans font-medium px-4 py-2.5 border-b border-primary/10">Transactions</th>
              <th className="text-right font-sans font-medium px-4 py-2.5 border-b border-primary/10">{win} change</th>
              <th className="text-right font-sans font-medium px-4 py-2.5 border-b border-primary/10">Trend</th>
            </tr>
          </thead>
          {loading && !items.length ? (
            <SkeletonRows rows={8} cols={6} />
          ) : (
            <tbody>
              {items.map((it, i) => (
                <tr key={it.asset} data-testid={`topasset-row-${i}`} className="border-b border-primary/5 last:border-b-0 hover:bg-primary/5 transition-colors">
                  <td className="px-4 py-2.5 text-right text-muted-foreground">{(page - 1) * COUNT + i + 1}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5 min-w-0 max-w-[230px]">
                      <TokenLogo item={it} size={26} testid={`topasset-avatar-${i}`} />
                      <AssetName item={it} onPick={onPickAsset} onOpenPolicy={onOpenPolicy} testid={`topasset-asset-${i}`} />
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground/80 text-[12px]">{it.fingerprint ? `${it.fingerprint.slice(0, 10)}…${it.fingerprint.slice(-4)}` : "—"}</td>
                  <td className="px-4 py-2.5 text-right text-foreground/90">{fmtInt(it.tx_count)}</td>
                  <td className="px-4 py-2.5 text-right"><Delta pct={it.delta_pct} testid={`topasset-delta-${i}`} /></td>
                  <td className="px-4 py-2.5 text-right"><TrendCell it={it} i={i} /></td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
      {error && !items.length ? <ErrorState error={error} onRetry={reload} testid="topassets-error" /> : null}
      {!loading && !error && !items.length ? <EmptyState label="No trending assets for this window." testid="topassets-empty" /> : null}
      {items.length ? <Pager page={page} setPage={setPage} hasNext={items.length >= COUNT} loading={loading} testid="topassets-pager" /> : null}
    </Panel>
  );
};
